import React, { useCallback, useEffect } from 'react';
import { ModalsKeys } from 'global';
import { logError } from 'utils/logger';
import { useModal } from 'hooks/useModal';
import { useForm } from 'react-hook-form';
import { useLazyQuery } from '@apollo/client';
import { customersApi } from 'modules/customers/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { CustomDialog } from 'components/shared/Dialog';
import { Button, CircularProgress, Stack } from '@mui/material';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { useAccountContact } from '../../api';
import { accountContactApi } from '../../api/contact';
import { CompanyInfoBlock } from './CompanyInfoBlock';
import { AccountContactFormFields } from './AccountContactFormFields';
import { GetAccountContactResponse, GetAccountContactVariables } from '../../api/contact/types';
import { mapFormToCreateInput, mapFormToUpdateInput, mapContactEntityToFormValues } from '../../mapper/accountContact';
import {
  accountContactFormSchema,
  AccountContactFormValues,
  accountContactFormDefaultValues,
} from './accountContactFormSchema';

export const AccountContactDialog = () => {
  const { isOpen, data, handleClose } = useModal(ModalsKeys.AccountContactDialog);

  const isEditMode = Boolean(data?.contactId);

  const [fetchContact, { loading: isContactLoading, error: contactError }] = useLazyQuery<
    GetAccountContactResponse,
    GetAccountContactVariables
  >(accountContactApi.byId, {
    onCompleted: response => {
      const contact = response?.accountQuery?.contact?.entity;

      if (contact) {
        reset(mapContactEntityToFormValues(contact));
      }
    },
  });

  const { handleCreateAccountContact, handleUpdateAccountContact, isCreating, isUpdating } = useAccountContact();

  const { control, handleSubmit, reset } = useForm<AccountContactFormValues>({
    resolver: zodResolver(accountContactFormSchema),
    defaultValues: accountContactFormDefaultValues,
  });

  useEffect(() => {
    if (isOpen && data?.contactId) {
      fetchContact({
        variables: {
          input: {
            id: data.contactId,
          },
        },
      }).catch(logError);
    } else {
      reset(accountContactFormDefaultValues);
    }
  }, [isOpen, data?.contactId, fetchContact, reset]);

  const refetchOptions = () => ({
    refetchQueries: [customersApi.getCustomerContacts],
    awaitRefetchQueries: true,
  });

  const onSubmit = useCallback(
    async (values: AccountContactFormValues) => {
      if (isEditMode && data?.contactId) {
        const dto = mapFormToUpdateInput(values, data.contactId);
        await handleUpdateAccountContact(dto, refetchOptions);
      } else {
        const dto = mapFormToCreateInput(values, data.accountId);
        await handleCreateAccountContact(dto, refetchOptions);
      }

      handleClose();
    },
    [data, isEditMode, handleCreateAccountContact, handleUpdateAccountContact, handleClose]
  );

  return (
    <CustomDialog
      maxWidth="lg"
      isOpen={isOpen}
      onClose={handleClose}
      label={`${isEditMode ? 'Update' : 'Add'} Contact`}
      actions={
        <>
          <Button disabled={isCreating || isUpdating} onClick={handleClose}>
            Cancel
          </Button>

          <Button variant="contained" loading={isCreating || isUpdating} onClick={handleSubmit(onSubmit)}>
            {isEditMode ? 'Update' : 'Add'} Contact
          </Button>
        </>
      }
    >
      <RenderWithFallback
        error={contactError}
        isLoading={isEditMode && isContactLoading}
        fallBack={
          <Stack alignItems="center" justifyContent="center" py={4}>
            <CircularProgress />
          </Stack>
        }
      >
        <Stack gap="1rem">
          <RenderWithCondition condition={!!data?.customerInfo}>
            <CompanyInfoBlock
              name={data?.customerInfo?.name}
              phone={data?.customerInfo?.phone}
              address={data?.customerInfo?.address}
            />
          </RenderWithCondition>

          <AccountContactFormFields control={control} />
        </Stack>
      </RenderWithFallback>
    </CustomDialog>
  );
};
